import { useForm } from 'react-hook-form';
import { Checkbox, FormProvider, SelectTag, Table, TextField } from '../../../../../components';
import {
  Container,
  Dialog,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
  TextField as MuiTextField,
  Box
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { dropdownFetch, getCurrentDateTime, getKeyPathValue, request, toast } from '../../../../../utils';
import CloseIcon from '@mui/icons-material/Close';
import { CustomButtonn } from '../../../../../components/Input/CustomButton';
import { useLocation } from 'react-router-dom';
import { OT_ENTRY_CHECKIN } from '../../../../../constants/dropdown';
import { formatRegTime } from '../../../../../utils';

const DETAIL_HEADER = [
  {
    header: 'Schedule Date',
    accessor: (row) => `${formatDate(row.date)}/${formatRegTime(row?.time)}`
  },
  {
    header: 'Patient Name',
    accessor: (row) => `${row.patient.name}`
  },
  {
    header: 'UHID No',
    accessor: 'uhid'
  },
  {
    header: 'IPD No',
    accessor: 'ipd_no'
  },
  {
    header: 'Doctor Name',
    accessor: (row) => row?.resource_detail.map((data) => data?.doctorName).join(', ')
  }
];

const TABLE_HEADER = [
  {
    header: 'UHID NO',
    accessor: 'uhid'
  },
  {
    header: 'IPD No.',
    accessor: 'ipd_no'
  },
  {
    header: 'Patient Name',
    accessor: (row) =>
      `${row?.patient?.name_suffix} ${row?.patient?.first_name} ${row?.patient?.last_name ? row?.patient?.last_name : ''}`
  },
  {
    header: 'Ward',
    accessor: (row) => row?.consultant_detail?.room?.ward?.name
  },
  {
    header: 'Bed No',
    accessor: (row) => row?.consultant_detail?.bed_no
  }
];

const initialIndoorRegisterationDataObj = {
  data: [],
  loading: false
};

function formatDate(dateString) {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

export const PatientTransfer = () => {
  const [detailList, setDetailList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentAction, setCurrentAction] = useState();
  const [selectedRecord, setSelectedRecord] = useState();
  const [indoorRegisterationDataObj, setIndoorRegisterationDataObj] = useState(initialIndoorRegisterationDataObj);
  const [openQueryDialog, setOpenQueryDialog] = useState(false);
  const { pathname } = useLocation();
  const lastPathSegment = pathname.split('/').pop();
  const [input, setInput] = useState('');
  const [isFiltered, setIsFiltered] = useState(false);
  const [originalData, setOriginalData] = useState([]);
  const [patientData, setPatientData] = useState([]);
  const [otDropdown, setOtDropdown] = useState([]);
  const user = JSON.parse(localStorage.getItem('user'));
  const isEnabledDate =
    user && user.permissions ? user.permissions.some((permission) => permission.includes('DatePermission')) : false;
  const methods = useForm({
    resolver: yupResolver(Yup.object().shape({})),
    defaultValues: {
      date: new Date().toISOString().split('T')[0],
      time: new Date().toLocaleTimeString('en-US', { hour12: false }).slice(0, 5)
    },
    mode: 'all'
  });
  const { handleSubmit, watch, reset, setValue } = methods;

  const formSubmit = async (formValue) => {
    try {
      const response = await request('/ot-patient-transfer-create', {
        method: 'POST',
        body : formValue,
      });
      if (response?.success) {
        toast(`Successfully ${'created'} Patient transfer`,{ variant: 'success' });
        setSelectedRecord(response?.data);
        setCurrentAction();
        searchIPDDetails();
        reset();
      } else {
        toast(`Failed to ${'create'} Patient Transfer`, { variant: 'error' });
      }
    } catch (error) {
      toast(`An error occurred: ${error.message}`, { variant: 'error' });
    }
  };




  useEffect(() => {
    if (indoorRegisterationDataObj.data && indoorRegisterationDataObj.data.length > 0) {
      setOriginalData(indoorRegisterationDataObj.data);
      setIsFiltered(false);
    }
  }, [indoorRegisterationDataObj.data]);

  const handleUhidSearch = () => {
    const filtered = indoorRegisterationDataObj.data.filter(
      (item) => item.uhid.toString().includes(input) || (item.ipd_no && item.ipd_no.toString().includes(input))
    );
    setOriginalData(filtered);
  };

  const handleChange = (e) => {
    const newInput = e.target.value;
    setInput(newInput);
    handleUhidSearch();
  };

  useEffect(() => {
    if (detailList && detailList.length > 0) {
      setPatientData(detailList);
      setIsFiltered(false);
    }
  }, [detailList]);

  const handlePatientSearch = () => {
    const filtered = detailList.filter(
      (item) =>
        item.patient.first_name === input ||
        item.uhid.toString().includes(input) ||
        (item.ipd_no && item.ipd_no.toString().includes(input))
    );
    setPatientData(filtered);
  };

  const handlePatientChange = (e) => {
    const newInput = e.target.value;
    setInput(newInput);
    handlePatientSearch();
  };

  useEffect(() => {
    searchIPDDetails();
  }, [input]);

  const searchIPDDetails = async () => {
    setLoading(true);
    const lowercasedInput = input.toLowerCase().trim();
    try {
      const query = {};
      if (lowercasedInput) {
        query.name = lowercasedInput;
      }
      const response = await request('/ot-schedule-list', {
        method: 'GET',
        query: query
      });
      if (response?.success) {
        if (!response?.data) {
          toast('No IPD found', { variant: 'warning' });
        } else {
          const sortedData = response.data;
          setDetailList(sortedData);
          setPatientData(sortedData);
        }
      } else {
        toast('Failed to load patient details', { variant: 'error' });
      }
    } catch (error) {
      toast('An error occurred while fetching patient details', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedRecord) {
      const keyValuePair = getKeyPathValue(selectedRecord, '', ['_id', 'status', '__v']);
      keyValuePair.forEach(({ key, value }) => setValue(key, value));
      const formattedDate = selectedRecord?.date ? new Date(selectedRecord.date).toISOString().split('T')[0] : '';
      setValue('date', formattedDate);
    } else {
      reset();
    }
  }, [setValue, reset, selectedRecord]);


  const getOtDropdown = useCallback(async () => {
    const data = await dropdownFetch('operation-theatre');
    setOtDropdown(data);
  }, []);

  useEffect(() => {
    getOtDropdown();
  }, [getOtDropdown]);

  return (
    <>
      <Paper
        sx={{
          p: 0,
          flex: 1,
          borderRadius: 0,
          boxShadow: 'none',
          width: '100%',
          height: 'auto'
        }}
      >
        <Container
          sx={{
            mt: 1,
            border: '0px solid',
            borderColor: 'grey.300',
            borderRadius: 1,
            width: '100%',
            boxSizing: 'border-box',
            minWidth: '100%'
          }}
        >
          <FormProvider methods={methods} onSubmit={handleSubmit(formSubmit)}>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="space-between"
              alignItems="center"
              sx={{
                mb: 1,
                borderBottom: '0px solid',
                borderColor: 'grey.300'
              }}
            >
              <Typography color="primary" variant="h3" sx={{ fontWeight: 600 }}>
                Patient Transfer
              </Typography>
              <Stack direction="row" justifyContent="flex-end" sx={{ mb: 3 }} spacing={1}>
                <CustomButtonn
                  id={`${lastPathSegment}Write`}
                  variant="contained"
                  disabled={['edit', 'new'].includes(currentAction)}
                  onClick={() => {
                    setSelectedRecord();
                    setCurrentAction('new');
                  }}
                >
                  New
                </CustomButtonn>
                <CustomButtonn
                  id={'Submit'}
                  type="submit"
                  variant="contained"
                  disabled={!['edit', 'new'].includes(currentAction) || loading}
                >
                  Save
                </CustomButtonn>
                <CustomButtonn
                  id={'Exit'}
                  variant="contained"
                  onClick={() => {
                    setCurrentAction('');
                  }}
                >
                  Exit
                </CustomButtonn>
              </Stack>
            </Stack>
            <Box sx={{ padding: 2, borderRadius: '8px', width: '80%', boxShadow: '0px 2px 10px #0099ff' }}>
              <Grid container spacing={2}>
                <Grid item xs={8} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Grid item xs={12} sx={{ display: 'flex', minHeight: '40rem', flexDirection: 'column', gap: 0.3 }}>
                    <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ mt: 2 }}>
                      <MuiTextField
                        sx={{
                          width: '100%',
                          '& .MuiOutlinedInput-root': {
                            padding: '0'
                          },
                          '& .MuiOutlinedInput-input': {
                            padding: '8px 14px',
                            fontSize: '1.5rem' 
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: '1.5rem' 
                          }
                        }}
                        label="Search Patient"
                        variant="outlined"
                        value={input}
                        onChange={handlePatientChange}
                      />
                    </Stack>
                    <Table
                      disabled
                      containerSX={{ flex: 1 }}
                      data={(currentAction === 'new' && patientData) || []}
                      columns={DETAIL_HEADER}
                      size="small"
                      onRowClick={(row) => {
                        const {
                          patient: { name },
                          ...otherDetails
                        } = row;
                        setSelectedRecord({
                          ...otherDetails,
                          patient_name: name,
                          ot_no: row?.otDetails[0]?._id
                        });
                        setOpenQueryDialog(false);
                      }}
                    />
                  </Grid>
                </Grid>
                <Grid
                  item
                  xs={4}
                  sx={{ display: 'flex', gap: 0.2, flexDirection: 'column', boxShadow: '0px 0px 2px #0099ff' }}
                >
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.35, fontWeight: 700, fontSize: '1.4rem' }}>Date</Typography>
                    <TextField
                      sx={{ flex: 0.8 }}
                      name="date"
                      type="date"
                      placeholder="Date"
                      disabled={!isEnabledDate || !['new', 'edit'].includes(currentAction)}
                    />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.35, fontWeight: 700, fontSize: '1.4rem' }}>Ipd No.</Typography>
                    <TextField sx={{ flex: 0.8 }} placeholder="Ipd No." name="ipd_no" disabled />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.35, fontWeight: 700, fontSize: '1.4rem' }}>Uhid No.</Typography>
                    <TextField sx={{ flex: 0.8 }} disabled name="uhid" placeholder="Uhid No." />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.35, fontWeight: 700, fontSize: '1.4rem' }}>Patient Name</Typography>
                    <TextField sx={{ flex: 0.8 }} name="patient_name" disabled placeholder="Patient Name" />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.35, fontWeight: 700, fontSize: '1.4rem' }}>Schedule No</Typography>
                    <TextField sx={{ flex: 0.8 }} name="schedule_no" disabled placeholder="Schedule No" />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.35, fontWeight: 700, fontSize: '1.4rem' }}>OT No</Typography>
                    <SelectTag
                      menus={otDropdown.map((data) => ({ ...data, name: data?.operationtheatre }))}
                      formControlSX={{ flex: 0.8 }}
                      name="optheatre"
                      placeholder="Operation Theatre"
                    />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.35, fontWeight: 700, fontSize: '1.4rem' }}>OT Entry</Typography>
                    <SelectTag
                      formControlSX={{ flex: 0.8 }}
                      name="ot_entry"
                      placeholder="OT Entry"
                      menus={OT_ENTRY_CHECKIN}
                    />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.35, fontWeight: 700, fontSize: '1.4rem' }}>Check In Time</Typography>
                    <TextField sx={{ flex: 0.8 }} name="check_in_time" type="time" disabled={watch('ot_entry') === "out"} />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.35, fontWeight: 700, fontSize: '1.4rem' }}>Check Out Time</Typography>
                    <TextField sx={{ flex: 0.8 }} name="check_out_time" type="time"disabled={watch('ot_entry') === "in"}  />
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </FormProvider>
        </Container>
      </Paper>
      <Dialog
        open={openQueryDialog}
        onClose={() => setOpenQueryDialog(false)}
        PaperProps={{sx:{ width: '80%',p: 2}}}
        maxWidth="xl"
        scroll="body"
      >
        <Stack
          padding={0}
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          borderBottom="1px solid"
          borderColor="grey.300"
        >
          <Typography variant="h4" fontWeight="bold">
            Patient Discharge Request Query Window
          </Typography>
          <IconButton onClick={() => setOpenQueryDialog(false)}>
            <CloseIcon color="error" fontSize="large" />
          </IconButton>
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ mt: 0 }}>
          <MuiTextField
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-root': {
                padding: '0',
                fontSize: '1.4rem' 
              },
              '& .MuiOutlinedInput-input': {
                padding: '8px 1.4rem'
              },
              '& .MuiInputLabel-root': {
                fontSize: '1.2rem' 
              }
            }}
            label="Search Patient"
            variant="outlined"
            value={input}
            onChange={handleChange}
          />
        </Stack>
        <Table
          sx={{ mt: 0.5 }}
          columns={TABLE_HEADER}
          data={indoorRegisterationDataObj?.data || []}
          loading={indoorRegisterationDataObj.loading}
          onRowClick={(row) => {
            const {
              patient: {
                name_suffix,
                first_name,
                last_name,
                sponsor: { name: sponsorName }
              },
              consultant_detail: { bed_no, room },
              ...otherDetails
            } = row;
            const {
              room_no,
              ward: { name: wardName }
            } = room;
            setSelectedRecord({
              ...otherDetails,
              patient_name: `${name_suffix} ${first_name} ${last_name}`,
              sponsor: sponsorName,
              bed_no: bed_no,
              room: room_no,
              ward_name: wardName,
              room_id: room._id, 
              date: new Date().toISOString().split('T')[0],
              time: new Date().toLocaleTimeString('en-US', { hour12: false }).slice(0, 5)
            });
            setOpenQueryDialog(false);
          }}
        />
      </Dialog>
    </>
  );
};
